import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/shared/components/ui/button";

interface PaginationProps {
  /** 1-based current page. */
  readonly page: number;
  readonly limit: number;
  readonly totalCount: number;
  readonly onPageChange: (page: number) => void;
  /** Prefix of the `data-testid`s (`<prefix>-pagination`, `-prev`, `-next`). */
  readonly testIdPrefix: string;
}

/**
 * Previous / next pager with a "Showing x-y of N" summary, for any listing
 * that returns `{ items, totalCount, page, limit }`. Copy lives under
 * `pagination.*`.
 */
export function Pagination({
  page,
  limit,
  totalCount,
  onPageChange,
  testIdPrefix,
}: PaginationProps) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  const from = totalCount === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, totalCount);

  return (
    <nav
      aria-label={t("pagination.label")}
      data-testid={`${testIdPrefix}-pagination`}
      className="flex items-center justify-between gap-3"
    >
      <p aria-live="polite" className="font-sans text-sm font-normal text-muted-foreground">
        {t("pagination.summary", { from, to, total: totalCount })}
      </p>
      <div className="flex items-center gap-2">
        <Button
          data-testid={`${testIdPrefix}-pagination-prev`}
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label={t("pagination.previous")}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </Button>
        <span className="font-sans text-sm font-medium tabular-nums text-foreground">
          {t("pagination.pageOf", { page, total: totalPages })}
        </span>
        <Button
          data-testid={`${testIdPrefix}-pagination-next`}
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label={t("pagination.next")}
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </nav>
  );
}
